import React, { useState, useEffect } from 'react';
import { Button, Form, Input, Select, DatePicker, Card, message, Row, Col, Modal, Checkbox } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useForm } from 'antd/es/form/Form';
import { useNavigate, useParams } from 'react-router-dom';
import { updateHoliday, getHolidayById } from '../../utils/API';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

interface EditHolidayModalProps {
    visible: boolean;
    onCancel?: () => void;
    onClose?: () => void;
    customerId?: string;
    onSuccess?: () => void;
}

const Editholidaymodal = (props: EditHolidayModalProps) => {
    const [form] = useForm();
    const { visible, onCancel, onClose, customerId } = props;
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [allowPastDates, setAllowPastDates] = useState(false);
    const { id } = useParams<{ id: string }>();

    useEffect(() => {
        const fetchHoliday = async () => {
            try {
                const response = await getHolidayById(customerId, {});
                const holidayData = response.data || response;

                if (!holidayData) {
                    throw new Error("No holiday data received");
                }

                // Parse the date
                let dateObj;
                if (holidayData.date.includes('/')) {
                    dateObj = dayjs(holidayData.date, 'DD/MM/YYYY');
                } else {
                    dateObj = dayjs(holidayData.date, 'DD-MM-YYYY');
                }

                // Check if the date is in the past
                const today = dayjs().startOf('day');
                const isPastDate = dateObj.isBefore(today);

                form.setFieldsValue({
                    type: holidayData.type,
                    date: dateObj.isValid() ? dateObj : null,
                    desc: holidayData.desc,
                    allowPastDates: isPastDate || holidayData.isPastDate || false
                });

                setAllowPastDates(isPastDate || holidayData.isPastDate || false);
            } catch (error) {
                console.error("Error fetching holiday:", error);
                message.error("Failed to load holiday data");
            } finally {
                setInitialLoading(false);
            }
        };

        if (customerId) fetchHoliday();
    }, [customerId, form]);

    const handleAllowPastDatesChange = (e: any) => {
        setAllowPastDates(e.target.checked);
    };

    const handleUpdate = async (values: any) => {
        setLoading(true);
        setIsLoading(true);
        try {
            const today = dayjs().startOf('day');
            const selectedDate = dayjs(values.date).startOf('day');

            // Check if the date is in the past
            const isPastDate = selectedDate.isBefore(today);

            // Validate date if not allowing past dates
            if (isPastDate && !values.allowPastDates) {
                message.error('Cannot update to past dates unless explicitly allowed');
                return;
            }

            const payload = {
                customerId: customerId,
                type: values.type,
                desc: values.desc,
                date: values.date?.format('DD-MM-YYYY'),
                allowPastDates: values.allowPastDates || false
            };

            await updateHoliday(customerId, payload);

            message.success({
                content: 'Holiday updated successfully!',
                duration: 2,
            });

            if (props.onSuccess) props.onSuccess();
            if (onClose) onClose();

            setTimeout(() => {
                navigate("/holidays");
            }, 100);

        } catch (error) {
            console.error("Update error:", error);
            if (error instanceof Error) {
                message.error(error.message || "Failed to update holiday");
            } else {
                message.error("Failed to update holiday");
            }
        } finally {
            setLoading(false);
            setIsLoading(false);
        }
    };

    return (
        <Modal
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={550}
            centered={false}
            destroyOnClose
            className="custom-modal"
            bodyStyle={{
                overflowY: "auto",
                fontFamily: "inter, sans-serif",
            }}
            style={{
                bottom: 20,
                top: 20,
                maxHeight: 'calc(100vh - 30px)',
                margin: '0 auto',
                position: 'relative',
            }}
            styles={{
                header: {
                    padding: "12px",
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#f1f5ffff",
                },
                content: {
                    padding: 0,
                    overflow: "hidden",
                },
            }}
            title={
                <div
                    style={{
                        color: "#4b5563",
                        fontFamily: "inter, sans-serif",
                        fontSize: "20px",
                        fontWeight: 500,
                    }}
                >
                    Edit Holiday
                </div>
            }
        >
            <Form
                onFinish={handleUpdate}
                form={form}
                layout="horizontal"
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 19 }}
                colon={false}
            >
                <div className="elements">
                    {/* Type */}
                    <Form.Item
                        label={<span style={{
                            color: '#4b5563',
                            fontFamily: 'inter, sans-serif',
                            fontSize: '14px',
                            paddingLeft: "15px",
                            fontWeight: 500,
                        }}>Type <span className="text-danger">*</span></span>}
                    >
                        <Form.Item
                            name="type"
                            rules={[{ required: true, message: 'Please select a type' }]}>
                            <Select
                                className='CustomSelect'
                                placeholder="Select type"
                                options={[
                                    { value: 'Holiday', label: 'Holiday' },
                                    { value: 'WeekOff', label: 'WeekOff' }
                                ]}
                            />
                        </Form.Item>
                    </Form.Item>

                    {/* Date */}
                    <Form.Item
                        label={<span style={{
                            color: '#4b5563',
                            fontFamily: 'inter, sans-serif',
                            fontSize: '14px',
                            paddingLeft: "15px",
                            fontWeight: 500,
                        }}>Date <span className="text-danger">*</span></span>}
                    >
                        <Form.Item
                            name="date"
                            rules={[{ required: true, message: 'Please select a date' }]}>
                            <DatePicker
                                format="DD/MM/YYYY"
                                className='Allinput'

                            />
                        </Form.Item>
                    </Form.Item>

                    {/* Description */}
                    <Form.Item
                        label={<span style={{
                            color: '#4b5563',
                            fontFamily: 'inter, sans-serif',
                            fontSize: '14px',
                            paddingLeft: "15px",
                            fontWeight: 500,
                        }}>Description <span className="text-danger">*</span></span>}
                    >
                        <Form.Item
                            name="desc"
                            rules={[{ required: true, message: 'Please enter a description' }]}>
                            <Input.TextArea rows={4} placeholder="E.g., New Year's Day" className='Allinputtextarea' />
                        </Form.Item>
                    </Form.Item>

                    {/* Allow Past Dates Checkbox */}
                    <Form.Item >
                        <Form.Item name="allowPastDates" valuePropName="checked" noStyle >
                            <Checkbox
                                onChange={handleAllowPastDatesChange}
                                checked={allowPastDates}
                            >
                                Allow updating to past dates
                            </Checkbox>
                        </Form.Item>
                    </Form.Item>
                </div>


                {/* Submit Button at Bottom */}
                <Row justify="end">
                    <Col
                        xs={24}
                        sm={24}
                        md={24}
                        xl={24}
                        xxl={20}
                        className="d-flex justify-content-end me-3"
                    >
                        <div className="button-wrapper">
                            <button
                                className="allcanclebutton"
                                onClick={onClose}
                                type='button'
                                disabled={isLoading}
                            >
                                Cancel
                            </button>

                            <button
                                className="allsubmitebutton"
                                type='submit'
                                disabled={isLoading}
                            >
                                {isLoading ? "Updating..." : "Update"}
                            </button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </Modal >
    );
};

export default Editholidaymodal;